/* eslint-disable react/jsx-no-useless-fragment */
import { BoxComponent } from '../../../../../base/box/Box';
import { memo, useEffect, useState } from 'react';
import Item from '../Item';
import {
  getLastItemIndexOfCategory,
  getScaleSize,
  getTemplate6Items,
  isBetweenRange,
} from '../../../../../utils/utils';
import { MenuCategoriesProps, Template } from '@zeus/shared';
import { TypographyComponent } from '../../../../../base/typography/Typography';
import { TEMPLATE_5_MODIFIERS } from '../../../../../utils/consts';
import { useNormalizedPixels } from '../../../../../hooks/useResize';
import CategoryBgImg from '../../../assets/imgs/category_bg.png';
import { Grid as GridComponent } from '../../../../../base19/grid/Grid';

interface CategoryProps {
  template: Omit<Template, 'content'>;
  category: MenuCategoriesProps | null;
  development?: boolean;
  timezone?: string;
  scale?: number;
  categoryIndex: number;
  fontFamily?: string;
}

const Category = ({
  template,
  development,
  category,
  timezone,
  categoryIndex,
  scale = 1,
  fontFamily: fontFamilyProp,
}: CategoryProps) => {
  const [active, setActive] = useState<boolean | null>(true);

  const { normalizePixels } = useNormalizedPixels();

  useEffect(() => {
    if (!development) {
      const intervalId = setInterval(() => {
        setActive(isBetweenRange(category?.schedules, timezone));
      }, 10000);

      return () => clearInterval(intervalId);
    }
  }, []);

  const sortedItems = getTemplate6Items(category, template?.itemsLimit, TEMPLATE_5_MODIFIERS, timezone, development);
  const hasAnyModifier = sortedItems.some((item) => item.hasValidModifiers);
  const isDevelopment = development ? true : isBetweenRange(category?.schedules, timezone);
  const lastItemIndex = getLastItemIndexOfCategory(sortedItems) ?? -1;
  const isCategoryEnabled = template?.category?.isEnabled ?? true;
  const isModifier = (hasAnyModifier || sortedItems.length === 0) && template?.itemPrice?.isEnabled;
  const fontFamily = fontFamilyProp || template?.fontFamilyName;

  return (
    active && (
      <>
        {isDevelopment && (
          <>
            {(isCategoryEnabled || isModifier) && (
              <BoxComponent
                sx={{
                  width: '48%',
                  height: 'auto',
                  maxWidth: '100%',
                  overflow: 'hidden',
                }}
              >
                {isCategoryEnabled && (
                  <BoxComponent
                    sx={{
                      backgroundImage: `url(${CategoryBgImg})`,
                      backgroundSize: '90% 100%',
                      backgroundRepeat: 'no-repeat',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <TypographyComponent
                      component={'h2'}
                      sx={{
                        color: template?.category?.color ? template?.category?.color : '#ffffff',
                        fontSize: getScaleSize(
                          normalizePixels(30 * Number(template?.category?.textScale ?? 1), 'height'),
                          scale
                        ),
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: '90%',
                        whiteSpace: 'nowrap',
                        '-webkit-line-clamp': '1',
                        '-webkit-box-orient': 'vertical',
                        fontFamily: fontFamily,
                        fontWeight: template.category?.fontWeight ?? '700',
                        fontStyle: template.category?.fontStyle,
                        letterSpacing: template.category?.letterSpacing,
                        lineHeight: template.category?.lineHeight,
                        paddingX: '5%',
                        paddingY: getScaleSize(normalizePixels(5, 'height'), scale),
                        textTransform: 'uppercase',
                      }}
                    >
                      {category?.name}
                    </TypographyComponent>
                  </BoxComponent>
                )}

                {/* Modifier options */}
                {isModifier && (
                  <BoxComponent
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginTop: getScaleSize(normalizePixels(20, 'height'), scale),
                    }}
                  >
                    <BoxComponent width={'50%'}>
                      <GridComponent container spacing={1}>
                        {TEMPLATE_5_MODIFIERS.map((val) => (
                          <GridComponent key={val} item size={6}>
                            <TypographyComponent
                              sx={{
                                fontWeight: '400',
                                textAlign: 'center',
                                fontFamily: fontFamily,
                                lineHeight: 'normal',
                                backgroundColor: 'black',
                                color: template?.item?.itemName?.color ? template.item.itemName.color : '#ffffff',
                                fontSize: getScaleSize(normalizePixels(16, 'height'), scale),
                                padding: `${getScaleSize(normalizePixels(5, 'height'), scale)} 0px`,
                              }}
                            >
                              {val}
                            </TypographyComponent>
                          </GridComponent>
                        ))}
                      </GridComponent>
                    </BoxComponent>
                  </BoxComponent>
                )}
              </BoxComponent>
            )}

            {sortedItems.map((item, index) => (
              <Item
                key={`${item.id}-${active}`}
                item={item}
                template={template}
                development={development}
                index={index}
                lastItemIndex={lastItemIndex}
                timezone={timezone}
                scale={scale}
                hasAnyModifier={hasAnyModifier}
                fontFamily={fontFamily}
                categoryId={category?.id || 0}
              />
            ))}
          </>
        )}
      </>
    )
  );
};

export default memo(Category);
